// Tiny bootstrap — keeps the team-picker render off the critical
// parse path. All the heavy systems (Three.js scene, PartyKit client,
// HUD, audio engine, lobby, match) live in ./app.ts and stream in
// via dynamic import() once the picker resolves. Vite code-splits at
// the import boundary, so a juror's first paint pays only for this
// module + the team-picker dependencies.
import { CONFIG } from './config';
import { getDeviceId } from './progression';
import { installCrashHandler } from './boot/crash';
import { installAudioUnlock } from './audio/synth';
import { promptTeam } from './ui/team_picker';
import { showIntroVideo, INTRO_VIDEO_FIRST, INTRO_VIDEO_SECOND } from './ui/intro_video';
import { mountFullscreenButton } from './ui/fullscreen_button';
import { mountOrientationPrompt } from './ui/orientation_prompt';
import { isFirstEverVisit, markVisited } from './boot/visit_count';
import { startIntroPrefetch } from './boot/progressive_loader';

const app = document.getElementById('app')!;
// Mark the body with the active theme id BEFORE any UI renders so
// theme-scoped CSS selectors (body[data-theme="y2k"] …) apply to the
// intro card, team picker, and any other boot-time overlays. Full
// resolution happens in `getActiveTheme()` later; this is a fast
// localStorage/URL read to unblock styling.
(() => {
  const qp = new URL(location.href).searchParams.get('theme');
  const ls = (() => { try { return localStorage.getItem('smp.theme'); } catch { return null; } })();
  document.body.dataset.theme = qp ?? ls ?? 'y2k';
})();
const deviceId = getDeviceId();
console.info(`[smp] v${CONFIG.VERSION} — device=${deviceId.slice(0, 8)} solo=${CONFIG.SOLO}`);
installCrashHandler();
installAudioUnlock();
// Boot order: entrance video → lobby. The procedural music is hard-
// muted via setIntroMusicMute for the video duration. App + lobby
// modules stream in IN PARALLEL underneath the video so cold-boot
// wall time is unchanged.
//
// First-ever visit gets entrance.mp4; every subsequent visit gets
// entrance2.mp4. The visit flag is set immediately so a refresh
// during the first video still counts as "first visit done".
//
// The legacy "VIRGIN VS CHAD" title splash (src/ui/title_splash.ts)
// is intentionally still in the repo and still callable; commit
// 4c1b75b is the canonical archive of the video → splash → lobby
// flow. Re-enable by re-adding `import { showTitleSplash }` and
// chaining `.then(() => { void showTitleSplash(); })` here.
// Mount the fullscreen toggle + portrait-rotation prompt FIRST, before
// the entrance video. The video sits at z=10001; the fullscreen button
// is at z=10002 so it stays tappable through the cinematic, and the
// orientation prompt is at z=10003 so portrait phones see "rotate
// your device" overlaid on the video instead of trying to watch it
// vertically. Both are idempotent — app.ts re-mounts them later as a
// safety net but the second call no-ops.
mountFullscreenButton(document.body);
mountOrientationPrompt(document.body);

const bootIntroSrc = isFirstEverVisit() ? INTRO_VIDEO_FIRST : INTRO_VIDEO_SECOND;
markVisited();
void showIntroVideo(bootIntroSrc);
promptTeam(app, deviceId, (name, role) => {
  import('./app').then(({ startApp }) => startApp(name, role)).catch((err) => {
    console.error('[smp] failed to load app chunk', err);
  });
});

// Defer the background prefetch one frame so the team picker paints
// first. Heavy lobby/match modules then stream in while the intro
// animates — they arrive warm in the browser cache before the player
// finishes picking a faction. `requestIdleCallback` is used where
// available so the fetch yields to pending user input.
const kickoff = (): void => startIntroPrefetch();
if ('requestIdleCallback' in window) {
  (window as { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
    .requestIdleCallback(kickoff, { timeout: 1500 });
} else {
  setTimeout(kickoff, 200);
}
